import { Inject, Injectable } from '@angular/core';
import { PipelineInterface, SalesPipelinesService, StageInterface } from '@vendasta/sales-opportunities';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { Pipeline } from '@vendasta/sales-common';
import { SelectedPipelineCacheService } from '../pipeline/cache/selected-pipeline-cache.service';
import { PARTNER_ID_TOKEN } from './tokens';

export const DEFAULT_PIPELINE_ID = 'DEFAULT';

@Injectable()
export class UserPipelines {
  defaultPipeline$: Observable<Pipeline>;
  private readonly partnerPipelines$$ = new ReplaySubject<Pipeline[]>(1);
  readonly partnerPipelines$ = this.partnerPipelines$$.asObservable();
  private readonly selectedPipelineID$$ = new ReplaySubject<string>(1);
  private readonly selectedPipelineID$ = this.selectedPipelineID$$.asObservable();

  static getUserDefaultPipeline(userPipelines: UserPipelines): Observable<Pipeline> {
    return userPipelines.defaultPipeline$;
  }

  static getPipelines(userPipelines: UserPipelines): Observable<Pipeline[]> {
    return userPipelines.partnerPipelines$;
  }

  static getCurrentPipeline(userPipelines: UserPipelines): Observable<Pipeline> {
    return userPipelines.getCurrentPipeline();
  }

  getCurrentPipeline(): Observable<Pipeline> {
    return this.defaultPipeline$.pipe(
      switchMap((defaultP) => {
        const selectedOrDefault$ = this.selectedPipelineID$.pipe(startWith(defaultP));
        return combineLatest([selectedOrDefault$, this.partnerPipelines$]);
      }),
      map(([selectedPipelineID, partnerPipelines]) => {
        return partnerPipelines.find((p) => p.id === selectedPipelineID);
      }),
    );
  }

  static isDefaultPipeline(pipelineID: string): boolean {
    return pipelineID === DEFAULT_PIPELINE_ID;
  }

  constructor(
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    private readonly pipelinesService: SalesPipelinesService,
    private readonly lastSelectedPipeline: SelectedPipelineCacheService,
  ) {
    partnerId$
      .pipe(
        take(1),
        switchMap((partnerId) => pipelinesService.list(partnerId)),
        map((pipelines) => {
          return this.getAvailablePipeline(pipelines);
        }),
        shareReplay(1),
      )
      .subscribe((p) => {
        this.partnerPipelines$$.next(p);
      });

    this.defaultPipeline$ = this.partnerPipelines$.pipe(
      map((pipelines) => {
        const lastViewedPipeline = this.lastSelectedPipeline.lastViewedPipeline;
        if (!lastViewedPipeline) {
          this.setCurrentPipelineID(pipelines[0].id);
          return pipelines[0];
        }
        const cachedPipeline = pipelines.find((p) => p.id === lastViewedPipeline);
        const pipeline = cachedPipeline ? cachedPipeline : pipelines[0];
        this.setCurrentPipelineID(pipeline.id);
        return pipeline;
      }),
      shareReplay(1),
    );
  }

  getAvailablePipeline(pipelines: PipelineInterface[]): Pipeline[] {
    if (!pipelines || pipelines.length === 0) {
      console.error('No pipelines available');
      return [];
    }

    if (pipelines.length > 1) {
      pipelines.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    }

    return pipelines.map((p) => this.convertToModelPipeline(p));
  }

  refreshPipelines(): Promise<Pipeline[]> {
    return this.partnerId$
      .pipe(
        take(1),
        switchMap((partnerId) => this.pipelinesService.list(partnerId)),
        map((pipelines) => {
          if (!pipelines || pipelines.length === 0) {
            console.error('No pipelines available');
            return [];
          }

          if (pipelines.length === 1 && pipelines[0].pipelineId === DEFAULT_PIPELINE_ID) {
            return [this.convertToModelPipeline(pipelines[0])];
          } else {
            if (pipelines.length > 1) {
              pipelines.sort((a, b) => {
                if (a.name < b.name) {
                  return -1;
                }
                if (a.name > b.name) {
                  return 1;
                }
                return 0;
              });
            }
            return pipelines
              .filter((p) => p.pipelineId !== DEFAULT_PIPELINE_ID)
              .map((p) => this.convertToModelPipeline(p));
          }
        }),
        tap((p) => this.partnerPipelines$$.next(p)),
        shareReplay(1),
      )
      .toPromise();
  }

  convertToModelPipeline(pipeline: PipelineInterface): Pipeline {
    const stages = pipeline.stages.map(
      (s) =>
        <StageInterface>{
          name: s.name,
          probability: s.probability || 0,
          stageId: s.stageId,
        },
    );
    stages.sort((a, b) => (a.probability < b.probability ? -1 : 1));
    return new Pipeline(stages, pipeline.pipelineId, pipeline.name);
  }

  public setCurrentPipelineID(pipelineID: string): void {
    this.selectedPipelineID$$.next(pipelineID);
    this.lastSelectedPipeline.setLastViewedPipeline(pipelineID);
  }
}

export function getDefaultUserPipeline(pipelines: PipelineInterface[]): PipelineInterface | null {
  if (!pipelines || pipelines.length === 0) {
    return null;
  }
  if (pipelines.length === 1) {
    return pipelines[0];
  }
  return pipelines.find((p) => p.pipelineId !== DEFAULT_PIPELINE_ID);
}
